/**
 * @file 启用微应用
 * @author gaoxunchen
 */

import _ from 'lodash';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { matchPath, useLocation } from 'react-router-dom';
import { registerMicroApps, start, addGlobalUncaughtErrorHandler, removeGlobalUncaughtErrorHandler, initGlobalState, MicroAppStateActions } from 'qiankun';
import { Loading } from '@tencent/ptp-fe-common';
import qs from 'qs';

import AccountSelect from '@/components/AccountSelect';
import { REACT_APP_PUBLIC_URL } from '@/constants/Constants';
import { getRainbowMicroAppConfig } from '@/api/rainbow';
import TaskHelp from '@/pages/agency/task/TaskCenter/components/task-help';
import globalStore from '@/stores';

// 格式化激活路径（英文逗号分隔【字符串】 => 单项去除收尾空格、去除无效项【数组】）
const formatValidRules = (rules: string) => {
  try {
    return _.reduce(JSON.parse(rules), (ret: any[], curRule) => {
      const curTrimRule = _.trim(curRule);
      if (curTrimRule.length > 0) {
        ret.push(curTrimRule);
      }
      return ret;
    }, []);
  } catch (error) {
    return [];
  }
};

let loadingStart = false;
// loading行为
const loader = (loading: boolean) => {
  if (loading === true && !loadingStart) {
    // 添加全局错误捕获
    addGlobalUncaughtErrorHandler(errorHandler);

    loadingStart = true;
    const loadingElem = document.querySelector('#microapp-loading');
    ReactDOM.render(<Loading />, loadingElem);
  } else if (loading === false && loadingStart) {
    // 移除全局错误捕获
    removeGlobalUncaughtErrorHandler(errorHandler);

    loadingStart = false;
    const loadingElem = document.querySelector('#microapp-loading');
    ReactDOM.unmountComponentAtNode(loadingElem);
  }
};

// 异常处理
const errorHandler = (error: any) => {
  console.error('Micro page error', error);
  // 避免部分请求失败页面闪现
  if (error?.type === 'unhandledrejection') {
    return;
  }
  // 重置loading状态
  loader(false);

  // eslint-disable-next-line react/no-render-return-value
  return ReactDOM.render(
    <div className="crm-error-boundary">
      <h1>
        出错了，
        <span className="text-active" onClick={() => history.replaceState(null, '', '/')}>返回首页</span>
      </h1>
    </div>,
    document.querySelector('#microapp-loading'),
  );
};

const originRemoveChild = HTMLBodyElement.prototype.removeChild;
// 微应用声明周期钩子
const microAppLifeCycles = {
  beforeMount: (app: any) => {
    // qiankun劫持方法基础上，再次proxy，为挂载到body的新节点，增加一层div data-qiankun嵌套
    // qiankun会在子应用销毁时重置成原生方法
    HTMLBodyElement.prototype.appendChild = new Proxy(HTMLBodyElement.prototype.appendChild, {
      apply(target, thisArg, node) {
        if (node[0].dataset.qiankun === app.name) {
          // 已有data-qiankun属性，直接使用原生方法
          Reflect.apply(target, thisArg, node);
        } else {
          // 创建新的div节点，作为子应用动态挂载节点的容器
          const newDom = document.createElement('div');
          // eslint-disable-next-line no-param-reassign
          newDom.dataset.qiankun = app.name;
          newDom.appendChild(node[0]);

          Reflect.apply(target, thisArg, [newDom]);
        }
      },
    });

    // qiankun劫持方法基础上，再次proxy，为挂载到body的新节点，增加一层div data-qiankun嵌套
    // qiankun会在子应用销毁时重置成原生方法
    HTMLBodyElement.prototype.removeChild = new Proxy(HTMLBodyElement.prototype.removeChild, {
      apply(target, thisArg, node) {
        const targetNode = node[0].parentNode;
        if (thisArg && thisArg.contains(targetNode)) {
          // 因为append时，真正挂载到body的是node的父节点（我们自己构造的）
          // 所以remove的时候，要移除node的父节点
          Reflect.apply(target, thisArg, [targetNode]);
        }
      },
    });
  },
  afterUnmount: (app: any) => {
    // 清除未移除干净的子应用动态挂载节点
    const microAppName = _.get(app, 'name');
    const curMicroAppDynamicNodes = document.body.querySelectorAll(`div[data-qiankun=${microAppName}]`);
    try {
      _.map(curMicroAppDynamicNodes, (node) => {
        // 只移除body下的子节点
        if (node.parentNode === document.body) {
          originRemoveChild.call(document.body, node);
        }
      });
    } catch (error) {
      console.error(`${microAppName} 子应用动态节点移除异常`, error);
    }
  },
};

// 创建全剧唯一 actions。actions不唯一会导致向子应用传入多次initialState
let actions: MicroAppStateActions;

export default (props: any) => {
  const { userInfo, ...otherProps } = props;
  const { pathname } = useLocation();
  const [fetchStatus, setFetchStatus] = useState<string>('');
  const [fetchResponse, setFetchResponse] = useState<Record<string, any>[]>([]);
  const { routes, pathSubNavMap = {} } = globalStore.account?.siteMap || {};

  // 路由配置
  const routeConfig: any = useMemo(() => {
    return routes.find((v: any) => matchPath(pathname, v.href)) || {};
  }, [pathname, routes]);

  const { accountSelect, showSideNav } = routeConfig;

  // 是否有三级导航
  const showSubNav = !!pathSubNavMap[pathname]?.length;

  // 初始化 state
  if (!actions) {
    actions = initGlobalState({
      // 注意，由于useMicroApp在AppContainer组件中的声明位置靠前，userInfo会不包含getCommonInfo对初始userInfo二次处理的结果（如：lazy loading 的灰度信息等）
      userInfo: _.omit(_.get(userInfo, 'account', {}), ['allOf', 'oneOf', 'siteMap']), // 传递给子应用的用户信息，去除主应用专有字段：allOf、oneOf、siteMap
      agencyIdList: AccountSelect.getAgencyList().map((v) => v.value || v),
      currentAgency: AccountSelect.getAgency(),
      currentAgencyList: AccountSelect.getAgencyList(),
    });
  }

  actions.onGlobalStateChange((state, prev) => {
    // state: 变更后的状态; prev 变更前的状态
    // console.log('[MICRO FRONT END] onGlobalStateChange', state, prev);
  });

  const handleAgencyIdChange = useCallback((_e: any, value: any) => {
    if (_.isArray(value)) {
      actions?.setGlobalState({
        currentAgencyList: AccountSelect.getAgencyList().filter((v) => value.includes(v.agency_uid)),
      });
    } else {
      actions?.setGlobalState({
        currentAgency: AccountSelect.getAgency(),
      });
    }
  }, []);

  // 请求无极配置
  useEffect(() => {
    try {
      (async () => {
        const data = await getRainbowMicroAppConfig();

        if (_.isArray(data)) {
          setFetchResponse(data);
        }
        setFetchStatus('success');
      })();
    } catch (error) {
      console.error(error);
    }
  }, []);

  // 是否是微前端页面
  const isMicroPage = useCallback((activeRule) => {
    return (location: Location) => {
      const { pathname } = location;
      const { isMicroApp } = (routes.find((v: any) => matchPath(pathname, `${REACT_APP_PUBLIC_URL}${v.href}`)) || {}) as any;
      // 路由配置中明确非微前端页面
      if (isMicroApp === false) {
        return false;
      }
      return _.some(activeRule, (rule) => _.startsWith(pathname, rule));
    };
  }, [routes]);

  // 微应用配置加载完成
  const microAppConfigLoaded = useMemo(() => (fetchStatus === 'success' || fetchStatus === 'error'), [fetchStatus]);
  // 微应用配置
  const microAppConfig = useMemo(() => _.reduce(fetchResponse, (prev: any[], config) => {
    const {
      active,
      name,
      activeRule,
      devEntry,
      testEntry,
      productionEntry,
      subRoutePrefix,
      featureFlag,
    } = config;

    if (active) {
      const entry = (() => {
        // 本地开发环境
        if (process.env.NODE_ENV === 'development') {
          return devEntry;
        }
        // 正式环境
        if (process.env.DEPLOY_ENV === 'production') {
          return productionEntry;
        }
        // 测试环境
        return testEntry;
      })();

      if (featureFlag) {
        const microAppFlag = _.get(userInfo, `feature_flag.${featureFlag}`, 1);
        if (!microAppFlag) {
          return prev;
        }
      }
      prev.push({
        name,
        entry,
        activeRule: isMicroPage(formatValidRules(activeRule)),  // formatValidRules(activeRule)
        featureFlag,
        container: '#microapp-container',
        props: {
          userInfo,
          userId: userInfo.user_id,
          agencyId: userInfo.agency_uid,
          agencyBMId: userInfo.bm_id,
          routePrefix: `${REACT_APP_PUBLIC_URL}${subRoutePrefix || ''}`,
          routerBaseName: `${REACT_APP_PUBLIC_URL}${subRoutePrefix || ''}`,
          ...otherProps,
        },
        loader,
        activeRuleList: formatValidRules(activeRule),
      });
    }
    return prev;
  }, []), [fetchResponse, userInfo, isMicroPage]);

  useEffect(() => {
    // 微应用配置拉取成功前，不进行初始化
    if (fetchStatus !== 'success') {
      return;
    }

    // 标记qiankun已启动，不再重复调用start
    // @ts-ignore
    if (!window.qiankunStarted) {
      console.log('[init] micro app');
      // @ts-ignore
      window.qiankunStarted = true;
      window.__MAIN_APP__ = 'agency';
      window.__BASE_PATH_NAME__ = '/ams';

      registerMicroApps(microAppConfig, microAppLifeCycles as any);
      start({
        prefetch: false,
        async fetch(url, option) {
          // spaui样式替换cdn地址, 避免子应用注入style图标出错;
          if (/public.gdtimg.com\/.+\/spaui.css/i.test(url as string)) {
            const res = await window.fetch(url as string, option);
            const txt = await res.text();
            const cdnCss = txt.replace(/url\(..\/..\/assets\//ig, 'url(https://public.gdtimg.com/static/onex/lib/spaui-components/assets/');
            const resp = new Response(cdnCss);
            return resp;
          }

          // 重构样式文件，替换图片cdn地址, 避免子应用注入style图标相对地址出错
          if (_.isString(url) && /qzonestyle\.gdtimg\.com\/.+\/manage-\d+\.css/i.test(url)) {
            const res = await window.fetch(url, option);
            const txt = await res.text();
            const cdnCss = txt.replace(
              /url\(.\/images\//gi,
              'url(https://qzonestyle.gdtimg.com/gdt_ui_proj/dist/spear/images/',
            );
            const resp = new Response(cdnCss);
            return resp;
          }

          // 转发test_feature等search参数
          const urlSearch = qs.parse(location.search.substring(1));
          // 只有入口链接才需要转发test_feature参数
          const urlString = !(url as string).startsWith('http') ? qs.stringify(_.reduce(
            urlSearch, (prev, value, key) => {
              if (_.includes(key, 'test_feature')) {
                prev[key] = value;
              }
              return prev;
            },
            {} as qs.ParsedQs,
          ), { addQueryPrefix: true }) : '';
          return window.fetch(`${url}${urlString}`, {
            ...option,
            cache: 'no-cache',
          });
        },
        sandbox: {
          // 改写微应用所有样式规则增加一个特殊的选择器规则来限定其影响范围
          // 最外层统一添加div[data-qiankun]
          experimentalStyleIsolation: true,
        },
      });
    }
  }, [fetchStatus, microAppConfig]);

  // 当前路径是否为微应用下路径
  const isMicroAppPath = useMemo(() => {
    // 微应用配置未加载完成，直接返回
    if (!microAppConfigLoaded) {
      return null;
    }
    const curPath = `${REACT_APP_PUBLIC_URL}${pathname}`;
    const microAppRules = _.flatten(_.map(microAppConfig, 'activeRuleList'));

    return isMicroPage(microAppRules)({ pathname: curPath } as Location);
    // return _.some(microAppRules, (microAppRule) => _.startsWith(curPath, microAppRule));
  }, [pathname, microAppConfigLoaded, microAppConfig, isMicroPage]);

  const basePageOptions = useMemo(() => {
    // 微应用配置未加载完成，直接返回
    if (!microAppConfigLoaded) {
      return {
        showSideNav: false, // 延迟展示侧边导航：微前端BasePage display=none时侧边导航计算有问题
      };
    }
    let options: any = {
      showSubNav,
      showSideNav,
    };
    if (accountSelect) {
      options = {
        accountSelectOptions: {
          onChange: handleAgencyIdChange,
          multiple: accountSelect !== 'single',
        },

      };
    }
    const curPath = `${REACT_APP_PUBLIC_URL}${pathname}`;
    const taskActiveRule = _.get(_.find(microAppConfig, (v) => v.name === 'agency-task'), 'activeRuleList', []);
    if (_.startsWith(curPath, taskActiveRule)) {
      return {
        ...options,
        tabNavOptions: {
          right: <TaskHelp />,
        },
      };
    }
    return options;
  }, [pathname, microAppConfigLoaded, microAppConfig, accountSelect, showSubNav, showSideNav]);

  return {
    pathname,
    microAppConfigLoaded,
    isMicroAppPath,
    microAppRules: _.flatten(_.map(microAppConfig, 'activeRuleList')),
    basePageOptions,
  };
};
